import React, { useState } from 'react'
import DayPicker, {
  DayPickerProps,
  CaptionElementProps,
} from 'react-day-picker'
import 'react-day-picker/lib/style.css'
import { SelectMonthYear } from './SelectMonthYear'

export interface DatePickerProps extends DayPickerProps {
  /**
   * Currently selected date
   */
  value: Date
  /**
   * These values are used to determined what year/month range to show in the header select menus.
   * If this prop is not provided, no select options will show to change visible month/year.
   */
  selectMonthYear?: {
    from: Date
    to: Date
  }
  /**
   * Since this is a single Date picker, ranges are not allowed. Use the value prop
   * to pass the selected day instead or use the DateRangePicker
   */
  selectedDays?: never
}

/**
 * Single date picker
 *
 * Intended to be a thin wrapper around react-day-picker to provide styling
 * and a controlled component.
 *
 * See the DatePickerInput for more Ellipsis specific functionality.
 */
// eslint-disable-next-line react/display-name
export const DatePicker = React.forwardRef<DayPicker, DatePickerProps>(
  ({ selectMonthYear, value, ...dayPickerProps }, ref) => {
    const disabledDays = (day: Date) =>
      selectMonthYear
        ? !DayPicker.DateUtils.isDayInRange(day, {
            from: selectMonthYear.from,
            to: selectMonthYear.to,
          })
        : false

    if (value && selectMonthYear && disabledDays(value)) {
      throw new Error(
        'Supplied value prop must be within bounds defined by selectMonthYear prop'
      )
    }

    const [displayMonth, setMonth] = useState(value || new Date())
    const captionElement = ({ date, localeUtils }: CaptionElementProps) => (
      <SelectMonthYear
        date={date}
        range={{
          from: selectMonthYear && selectMonthYear.from,
          to: selectMonthYear && selectMonthYear.to,
        }}
        localeUtils={localeUtils}
        onChange={setMonth}
      />
    )

    return (
      <DayPicker
        ref={ref}
        {...(selectMonthYear && { captionElement })}
        {...dayPickerProps}
        modifiersStyles={{
          selected: {
            borderRadius: 3,
            color: '#888',
            backgroundColor: '#e3e3e3',
          },
        }}
        {...(selectMonthYear && {
          fromMonth: selectMonthYear.from,
          toMonth: selectMonthYear.to,
        })}
        month={displayMonth}
        selectedDays={value || undefined}
        disabledDays={
          Array.isArray(dayPickerProps.disabledDays)
            ? [disabledDays, ...dayPickerProps.disabledDays]
            : [disabledDays, dayPickerProps.disabledDays]
        }
      />
    )
  }
)
